import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, query, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaTrash, FaEdit, FaSave, FaTimes, FaUserShield, FaPlus } from "react-icons/fa";
import { getFunctions, httpsCallable } from "firebase/functions";

const CnfManager = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [cnfProfiles, setCnfProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(null);

    const initialForm = {
        id: "",
        name: "",
        gst: "",
        addr1: "",
        addr2: "",
        addr3: "",
        addr4: "",
        state: "",
        stateCode: "",
        pan: "",
        remarks: ""
    };
    const [form, setForm] = useState(initialForm);

    const [allUsers, setAllUsers] = useState([]);
    const [isUpdatingRole, setIsUpdatingRole] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (!["superadmin", "admin"].includes(profile.role)) {
                    alert("Unauthorized");
                    navigate("/dashboard");
                    return;
                }
                setUserProfile(profile);
                loadCnf();
                loadUsers();
            }
        });
        return () => unsub();
    }, [navigate]);

    const loadCnf = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "cnf_master"));
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setCnfProfiles(list);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const q = query(collection(db, "users"));
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
            setAllUsers(list);
        } catch (err) {
            console.error("Error loading users:", err);
        }
    };

    const updateRole = async (targetUid, newRole) => {
        const ok = window.confirm(`Assign "${newRole.toUpperCase()}" role to this user?`);
        if (!ok) return;

        setIsUpdatingRole(true);
        try {
            await updateDoc(doc(db, "users", targetUid), { role: newRole });
            alert("Role updated successfully.");
            loadUsers();
        } catch (err) {
            console.error("Error updating role:", err);
            alert("Failed to update role. Verify Firestore permissions.");
        } finally {
            setIsUpdatingRole(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.gst) { alert("Name and GST are required"); return; }
        try {
            const cid = form.id || form.gst.toUpperCase().substring(0, 15);
            await setDoc(doc(db, "cnf_master", cid), {
                ...form,
                id: cid,
                updatedAt: serverTimestamp()
            });
            alert("C&F Profile Saved");
            setForm(initialForm);
            setIsEditing(null);
            loadCnf();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleEdit = (c) => {
        setForm(c);
        setIsEditing(c.id);
        window.scrollTo(0, 0);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this C&F Profile?")) return;
        await deleteDoc(doc(db, "cnf_master", id));
        loadCnf();
    };

    const handleOnboardStaff = (e) => {
        // Obsolete - keeping for logic reference if needed, but not mapping any more.
    };

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40 }}>
                    <h1 style={{ fontSize: 32, fontWeight: 900 }}>C&F MASTER SETUP</h1>
                    <p style={{ color: "var(--text-muted)" }}>Configure Carrying & Forwarding entities and primary warehouse identifiers.</p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 32 }}>
                    
                    {/* LIST */}
                    <div className="premium-card" style={{ padding: 0 }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th>C&F Entity</th>
                                    <th>Tax Identity</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cnfProfiles.map(c => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ fontWeight: 800 }}>{c.name}</div>
                                            <div style={{ fontSize: 11, opacity: 0.6 }}>{c.state}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 12, fontWeight: 700 }}>{c.gst}</div>
                                            <div style={{ fontSize: 10 }}>PAN: {c.pan}</div>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", gap: 10 }}>
                                                <button onClick={() => handleEdit(c)} className="btn-secondary" style={{ padding: 8 }}><FaEdit /></button>
                                                <button onClick={() => handleDelete(c.id)} className="btn-secondary" style={{ padding: 8, color: "var(--error)" }}><FaTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* FORM */}
                    <div style={{ position: "sticky", top: 100 }}>
                        <div className="premium-card">
                            <h3 style={{ marginBottom: 20, fontSize: 14, fontWeight: 800 }}>{isEditing ? "EDIT C&F PROFILE" : "NEW C&F PROFILE"}</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <div><label style={labelS}>C&F Name</label><input className="form-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} /></div>
                                <div><label style={labelS}>GSTIN</label><input className="form-input" value={form.gst} onChange={e=>setForm({...form, gst: e.target.value.toUpperCase()})} /></div>
                                
                                <div>
                                    <label style={labelS}>Warehouse Address</label>
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 1" value={form.addr1} onChange={e=>setForm({...form, addr1: e.target.value})} />
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 2" value={form.addr2} onChange={e=>setForm({...form, addr2: e.target.value})} />
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 3" value={form.addr3} onChange={e=>setForm({...form, addr3: e.target.value})} />
                                    <input className="form-input" placeholder="Line 4" value={form.addr4} onChange={e=>setForm({...form, addr4: e.target.value})} />
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <div><label style={labelS}>State</label><input className="form-input" value={form.state} onChange={e=>setForm({...form, state: e.target.value})} /></div>
                                    <div><label style={labelS}>State Code</label><input className="form-input" value={form.stateCode} onChange={e=>setForm({...form, stateCode: e.target.value})} /></div>
                                </div>

                                <div><label style={labelS}>PAN</label><input className="form-input" value={form.pan} onChange={e=>setForm({...form, pan: e.target.value.toUpperCase()})} /></div>
                                <div><label style={labelS}>Remarks</label><textarea className="form-input" value={form.remarks} onChange={e=>setForm({...form, remarks: e.target.value})} /></div>

                                <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                                    <button onClick={handleSave} className="btn-primary" style={{ flex: 1 }}><FaSave /> SAVE C&F</button>
                                    {isEditing && <button onClick={() => { setIsEditing(null); setForm(initialForm); }} className="btn-secondary"><FaTimes /></button>}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* --- SYSTEM PERSONNEL REGISTRY --- */}
                <div style={{ marginTop: 60, marginBottom: 40 }}>
                    <h3 style={{ fontSize: 20, fontWeight: 900, marginBottom: 8 }}>PLATFORM PERSONNEL REGISTRY</h3>
                    <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 24 }}>Manage internal access levels. Assign roles to registered users for system-wide access control.</p>

                    <div className="premium-card" style={{ padding: 0 }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th>User Identity</th>
                                    <th>Assigned Role</th>
                                    <th style={{ textAlign: "right" }}>Staff Authorization</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allUsers.length === 0 ? (
                                    <tr><td colSpan="3" style={{ textAlign: "center", padding: 40, opacity: 0.5 }}>Syncing personnel manifest...</td></tr>
                                ) : (
                                    allUsers.map(user => (
                                        <tr key={user.uid}>
                                            <td>
                                                <div style={{ fontWeight: 800 }}>{user.name || "Anonymous User"}</div>
                                                <div style={{ fontSize: 11, opacity: 0.6 }}>ID: {user.email || user.uid}</div>
                                            </td>
                                            <td>
                                                <div className={`badge ${user.role === 'ds_manager' ? 'badge-success' : 'badge-warning'}`} style={{ display: "inline-block" }}>
                                                    {user.role ? user.role.replace("_", " ").toUpperCase() : "NO ROLE"}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {user.role !== 'ds_manager' ? (
                                                    <button 
                                                        disabled={isUpdatingRole}
                                                        onClick={() => updateRole(user.uid, 'ds_manager')}
                                                        className="btn-primary" 
                                                        style={{ fontSize: 10, padding: "8px 16px" }}
                                                    >
                                                        <FaUserShield /> PROMOTE TO DS MANAGER
                                                    </button>
                                                ) : (
                                                    <button 
                                                        disabled={isUpdatingRole}
                                                        onClick={() => updateRole(user.uid, 'operator')}
                                                        className="btn-secondary" 
                                                        style={{ fontSize: 10, padding: "8px 16px", color: "var(--danger)" }}
                                                    >
                                                        REVOKE ACCESS
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const labelS = { fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4, display: "block" };

export default CnfManager;
